import { Box, Button, Divider, TextField, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import React, { useEffect } from "react";
import {
  Control,
  Controller,
  useFieldArray,
  UseFormSetValue,
  useWatch,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ECountry } from "../../../shared/enums/user/ECountry";
import { IClient } from "../../../shared/interfaces/client/IClient";
import {
  IInvoice,
  IInvoiceItem,
} from "../../../shared/interfaces/invoice/IInvoice";
import { lightPalette } from "../../../shared/mui/palettes";
import InvoiceFooterCalculation from "./InvoiceFooterCalculation";
import InvoiceItem from "./InvoiceItem";

interface IProps {
  control: Control<IInvoice>;
  setValue: UseFormSetValue<IInvoice>;
  clients: IClient[];
}
const InvoiceItemDetails = ({ control, setValue, clients }: IProps) => {
  const { t } = useTranslation();
  const { fields, append, remove } = useFieldArray({
    control,
    name: "items",
  });

  const { taxes, discount, items, clientId } = useWatch({
    control,
  });

  useEffect(() => {
    if (clientId && clients?.length) {
      const selectedClient = clients?.find((client) => client?.id === clientId);
      setValue("taxes", selectedClient?.country === ECountry.BOSNIA ? 17 : 0);
      setValue(
        "items",
        selectedClient?.modules?.map((module) => ({
          name: t(`modules.${module?.name}`),
          quantity: 1,
          price: module?.price,
          description: module?.description,
        })) as IInvoiceItem[],
      );
    }
    // eslint-disable-next-line
  }, [clientId]);
  return (
    <>
      <Grid container spacing={2} sx={{ alignItems: "start", marginY: "1rem" }}>
        <Grid size={{ xs: 12, md: 5 }}>
          <h3>{t("invoice.services")}:</h3>
          <Typography variant={"subtitle1"} color={lightPalette.text.gray}>
            {t("invoice.addItems")}
          </Typography>
        </Grid>

        <Grid size={{ xs: 12, md: 7 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 12 }}>
              {fields.map((item, index) => (
                <InvoiceItem
                  index={index}
                  item={item}
                  key={index}
                  control={control}
                  remove={remove}
                />
              ))}

              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Button
                  variant="contained"
                  sx={{ flex: 1 }}
                  onClick={() =>
                    append({
                      name: "",
                      quantity: 1,
                      price: 0,
                      description: "",
                    } as IInvoiceItem)
                  }
                >
                  + {t("button.addItem")}
                </Button>
              </Box>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Divider />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                name={"taxes"}
                control={control}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <TextField
                    error={!!error?.message}
                    helperText={t(error?.message as string)}
                    value={value ?? ""}
                    type={"number"}
                    onChange={onChange}
                    label={t("invoice.taxes")}
                    sx={{ width: "100%" }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                name={"discount"}
                control={control}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <TextField
                    error={!!error?.message}
                    helperText={t(error?.message as string)}
                    value={value ?? ""}
                    onChange={onChange}
                    type={"number"}
                    label={t("invoice.discount")}
                    sx={{ width: "100%" }}
                  />
                )}
              />
            </Grid>
            <Grid
              size={{ xs: 12 }}
              sx={{ display: "flex", justifyContent: "flex-end" }}
            >
              <Box sx={{ width: { xs: "100%", md: "50%" } }}>
                <InvoiceFooterCalculation
                  items={items as IInvoiceItem[]}
                  discount={Number(discount)}
                  taxes={Number(taxes)}
                />
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default InvoiceItemDetails;
